import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import { getConfig } from "../config";

export default function LoginPage({
  onSignIn,
  onLocalLogin,
}: {
  onSignIn: () => void;
  onLocalLogin?: (token: string) => void;
}): React.ReactElement {
  const envName = getConfig().envName;
  const apiUrl = getConfig().apiUrl.replace(/\/$/, "");
  const theme = useTheme();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLocalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onLocalLogin) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Login failed (${res.status})`);
      }
      const { token } = await res.json();
      onLocalLogin(token);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a1628 0%, #1a2332 50%, #0d2137 100%)"
            : "linear-gradient(135deg, #e8f4fd 0%, #f0f4f8 50%, #e1ecf7 100%)",
      }}
    >
      <Card
        elevation={8}
        sx={{
          maxWidth: 420,
          width: "100%",
          mx: 2,
          borderRadius: 3,
          overflow: "visible",
          position: "relative",
          pt: 5,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -32,
            left: "50%",
            transform: "translateX(-50%)",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #0066CC, #0052a3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 20px rgba(0, 102, 204, 0.4)",
          }}
        >
          <RssFeedIcon sx={{ color: "#fff", fontSize: 32 }} />
        </Box>

        <CardContent sx={{ textAlign: "center", px: 4, pb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            SMRT Link News Feed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Admin Console
          </Typography>

          {envName && (
            <Typography
              variant="caption"
              sx={{
                display: "inline-block",
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 3,
                bgcolor:
                  envName === "prod"
                    ? "error.main"
                    : envName === "qa"
                      ? "warning.main"
                      : "info.main",
                color: "#fff",
              }}
            >
              {envName}
            </Typography>
          )}

          <Box sx={{ mt: envName ? 0 : 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<LockOutlinedIcon />}
              onClick={onSignIn}
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                background: "linear-gradient(135deg, #0066CC, #0052a3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0052a3, #003d7a)",
                },
              }}
            >
              Sign in with Okta
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              or
            </Typography>
          </Divider>

          <Box
            component="form"
            onSubmit={handleLocalSubmit}
            sx={{ textAlign: "left" }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              size="small"
              autoComplete="username"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="current-password"
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="outlined"
              fullWidth
              disabled={loading || !username || !password}
              sx={{
                py: 1,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {loading ? "Signing in..." : "Sign in with credentials"}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mt: 3 }}
          >
            Authorized PacBio personnel only
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
